/**
 * "What we've learned about you" — editable surface for the user's learned
 * insights on the agent-guidance settings page. Mirrors the editable rows in
 * the welcome onboarding proposal: each field is a labeled textarea/input
 * that auto-saves on blur via PATCH and is disabled while the learning agent
 * is still analyzing.
 *
 * Visual style intentionally matches the existing agent_guidance textarea
 * (boxed ring/shadow) rather than the proposal card's inline-edit feel —
 * users land here from a navigation menu, so the fields should look like
 * proper form fields.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import {
  type InsightItem,
  useLearnedInsights,
  useUpdateLearnedInsight,
} from '@/hooks/profile/useLearnedInsights';
import { QueryErrorState } from '@/lib/components';
import { logger } from '@/lib/logger';
import { ANALYZING_PLACEHOLDER } from '@/lib/proposals/analyzingPlaceholder';

import { useUser } from '../../../../../allauth_auth';
import { LearnedInsightField } from './LearnedInsightField';

const SECTION_HEADER = 'What we’ve learned about you';
const SECTION_DESCRIPTION =
  'Based on your inbox and calendar, here’s how AI agents currently see you. Edit anything that doesn’t match how you actually work – these are reused everywhere AI helps you.';

interface SaveHandler {
  (term: string, value: string): Promise<void>;
}

export function LearnedInsightsCard() {
  const { currentOrg } = useOrg();
  const user = useUser();
  const orgId = currentOrg?.id;
  const userId = user?.id;

  const {
    data,
    isLoading,
    error: loadError,
    refetch,
  } = useLearnedInsights(orgId, userId);
  const { mutateAsync: updateInsight } = useUpdateLearnedInsight(orgId, userId);

  const handleSave = useCallback<SaveHandler>(
    async (term, value) => {
      if (!orgId || !userId) {
        // Should be unreachable — the hooks are disabled without these — but
        // keep the row's draft consistent if it ever happens.
        const message = `Couldn’t save ${term}. Try again in a moment.`;
        toast.error(message);
        throw new Error(message);
      }
      try {
        await updateInsight({ term, value });
      } catch (err) {
        logger.error(
          'Failed to save learned insight from settings',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'settings', orgId, term }
        );
        toast.error(`Couldn’t save ${term}. Try again in a moment.`);
        // Re-throw so the row reverts its draft to the last known server value.
        throw err;
      }
    },
    [orgId, userId, updateInsight]
  );

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  return (
    <div>
      <h3 className='text-base font-semibold text-fg-primary'>
        {SECTION_HEADER}
      </h3>
      <p className='text-sm text-fg-tertiary mt-0.5'>{SECTION_DESCRIPTION}</p>

      <div className='mt-4 space-y-5'>
        {loadError && !data ? (
          // Only block the UI on a load error when we have no cached data to
          // show. React Query keeps the previous successful payload on
          // background refetch failures — surface that stale-but-valid data
          // rather than hiding it behind a refresh-the-page message.
          <QueryErrorState
            error={loadError}
            label='Couldn’t load your learned insights'
            onRetry={handleRetry}
            size='sm'
          />
        ) : (
          <LearnedInsightsFields
            items={data?.items}
            isLoading={isLoading}
            onSave={handleSave}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Renders the list of insight rows. Split from `LearnedInsightsCard` so the
 * loading/empty/loaded branches are explicit and the parent stays a thin
 * data-fetch container.
 */
function LearnedInsightsFields({
  items,
  isLoading,
  onSave,
}: {
  items: InsightItem[] | undefined;
  isLoading: boolean;
  onSave: SaveHandler;
}) {
  if (isLoading || !items) {
    // Skeleton-ish placeholder — three rows match the expected count. Keeps
    // the section from popping in layout when the GET resolves.
    return (
      <>
        {SKELETON_ITEMS.map(item => (
          <LearnedInsightField
            key={item.term}
            item={item}
            onSave={onSave}
            isInitialLoading
          />
        ))}
      </>
    );
  }

  if (items.length === 0) {
    return (
      <p className='text-sm text-fg-tertiary'>
        Nothing learned yet – we’ll fill these in as agents finish analyzing
        your inbox.
      </p>
    );
  }

  return (
    <>
      {items.map(item => (
        <LearnedInsightField key={item.term} item={item} onSave={onSave} />
      ))}
    </>
  );
}

// Skeleton placeholders mirror the three INSIGHT_FIELDS terms so the section
// is the right height while loading. The values shown are placeholders only
// — the field is disabled and the real definitions replace them once the
// GET resolves.
const SKELETON_ITEMS: InsightItem[] = [
  {
    term: 'Role',
    definition: ANALYZING_PLACEHOLDER,
    editable: true,
    multiline: true,
  },
  {
    term: 'Focus',
    definition: ANALYZING_PLACEHOLDER,
    editable: true,
    multiline: true,
  },
  {
    term: 'Work patterns',
    definition: ANALYZING_PLACEHOLDER,
    editable: true,
    multiline: true,
  },
];
